import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { tasksApi } from '../tasks.api';
import type { Meta, TaskDetail } from '../tasks.types';
import { errorMessage, fmtHours, num } from '../tasks.utils';
import DialogShell from './DialogShell';
import EmployeePicker from './EmployeePicker';
import { useCapacityConfirm, wasCancelled } from './useCapacityConfirm';

/** Give an unowned task (backlog / released) to someone. */
export default function AssignDialog({
  open,
  meta,
  detail,
  onClose,
  onDone,
}: {
  open: boolean;
  meta: Meta;
  detail: TaskDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const left = num(detail.hours.taskRemaining);
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [hours, setHours] = useState('');
  const [start, setStart] = useState('');
  const [due, setDue] = useState('');
  const [requireAcceptance, setRequireAcceptance] = useState(true);
  const [busy, setBusy] = useState(false);
  const { run, dialog } = useCapacityConfirm();

  useEffect(() => {
    if (!open) return;
    setEmployeeId(null);
    setHours(left > 0 ? String(left) : '');
    setStart(detail.task.startDate ?? '');
    setDue(detail.task.dueDate ?? '');
    setRequireAcceptance(true);
  }, [open, detail, left]);

  const submit = async () => {
    if (!employeeId) return toast.error('Choose an employee.');
    setBusy(true);
    try {
      await run((allowOverCapacity) =>
        tasksApi.assign(detail.task.id, {
          employeeId,
          allocatedHours: hours === '' ? null : Number(hours),
          startDate: start || null,
          dueDate: due || null,
          requireAcceptance,
          allowOverCapacity,
        })
      );
      toast.success('Task assigned');
      onDone();
    } catch (e) {
      if (!wasCancelled(e)) toast.error(errorMessage(e, 'Could not assign'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogShell open={open} title={`ASSIGN ${detail.task.taskCode}`} onClose={onClose} onSubmit={submit} submitLabel='Assign' busy={busy}>
        <Typography variant='body2' color='text.secondary'>
          {fmtHours(left)} of the task’s {fmtHours(detail.hours.taskAllocated)} budget is unassigned. Hours are planned evenly over the working days
          between start and due date.
        </Typography>
        <EmployeePicker employees={meta.employees} value={employeeId} onChange={setEmployeeId} required />
        <TextField label='Allocated hours' type='number' size='small' inputProps={{ min: 0, step: 0.25 }} value={hours} onChange={(e) => setHours(e.target.value)} />
        <Stack direction='row' gap={2}>
          <TextField label='Start' type='date' size='small' fullWidth InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} />
          <TextField label='Due' type='date' size='small' fullWidth InputLabelProps={{ shrink: true }} value={due} onChange={(e) => setDue(e.target.value)} />
        </Stack>
        <FormControlLabel control={<Switch checked={requireAcceptance} onChange={(e) => setRequireAcceptance(e.target.checked)} />} label='Employee must accept' />
      </DialogShell>
      {dialog}
    </>
  );
}
